<template>
    <form>
        <div class="row q-col-gutter-md">
            <div class="col-12">
                <q-item v-if="editMode">
                    <q-item-section>
                        <q-item-label><b>Sender:</b> {{ TransferObj.user.name }}</q-item-label>
                        <q-item-label caption><b>Reference:</b> {{ TransferObj.reference }}</q-item-label>
                    </q-item-section>
                </q-item>

                <q-select
                    v-model="TransferObj.user_id"
                    :options="users"
                    use-input
                    hide-selected
                    fill-input
                    input-debounce="500"
                    option-value="id"
                    option-label="name"
                    emit-value
                    map-options
                    clearable
                    :label="$t('transfers.user')"
                    :error="TransferObj.errors.has('user_id')"
                    color="primary"
                    @filter="filterUsers"
                    v-else
                >
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                            style="border-bottom:1px solid #999"
                            @click="grabUser(scope.opt)"
                        >
                            <q-item-section top>
                                <q-item-label lines="1">
                                    <span class="text-green">{{ scope.opt.id }} - </span>
                                    <span
                                        class="text-weight-medium text-uppercase">{{ scope.opt.name }}</span>
                                </q-item-label>
                                <q-item-label caption lines="1">
                                    {{ scope.opt.email }}
                                </q-item-label>
                                <q-item-label lines="1" class="text-body2 text-weight-bold text-primary">
                                    <span>{{ scope.opt.mobile }}</span>
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                    <template v-slot:no-option>
                        <q-item>
                            <q-item-section class="text-grey">
                                No User Found!
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
            </div> <!-- SENDER (USER) -->
            <div class="col col-xs-12 col-md-4 col-lg-4">
                <q-input
                    v-model="TransferObj.origin_amount"
                    :label="$t('transfers.origin_amount')"
                    type="number"
                    input-class="text-center input-bold"
                    color="primary"
                    :error="TransferObj.errors.has('origin_amount')"
                    :error-message="TransferObj.errors.get('origin_amount')"
                    :rules="[ val => val && val > 0 || $t('transfers.origin_amount') + ' is required!']"
                    @input="calculateRateOrDestAmount"
                    buttom-slots
                    :readonly="locked_input === 'origin'"
                >
                    <template v-slot:prepend>
                        <q-btn
                            flat
                            round
                            :icon="locked_input === 'origin' ? 'fa fa-lock' : 'fa fa-lock-open'"
                            size="xs"
                            :color="locked_input === 'origin' ? 'primary' : 'gray'"
                            @click="lock('origin')"
                        />
                    </template>
                    <template v-slot:append>
                        <span class="text-subtitle1 text-bold text-secondary" v-if="editMode">{{ TransferObj.origin_currency }}</span>
                        <q-btn-dropdown
                            v-model="displayOrgCurrencies"
                            :label="originCurrency ? originCurrency.symbol : '?'"
                            :icon="originCurrency ? originCurrency.country : ''"
                            flat
                            dense
                            v-else
                        >
                            <q-list>
                                <q-item clickable v-close-popup v-for="org_currency in origin_currencies" :key="org_currency.acronym" @click="setTransferOriginCurrency(org_currency)">
                                    <q-item-section avatar>
                                        <q-icon :name="org_currency.country" size="36px" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label>{{org_currency.currency}}</q-item-label>
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </q-btn-dropdown>
                    </template>
                    <template v-slot:hint>
                        <currency-format
                            class="inline-block"
                            :value="TransferObj.origin_amount"
                            :unit="TransferObj.origin_currency"
                            hide-white-spaces
                            hide-zero-decimal
                        />
                    </template>
                </q-input>
            </div> <!-- SENT AMOUNT -->
            <div class="col col-xs-12 col-md-4 col-lg-4">
                <q-input
                    v-model="TransferObj.rate"
                    :label="$t('transfers.rate')"
                    lazy-rules
                    :error="TransferObj.errors.has('rate')"
                    :error-message="TransferObj.errors.get('rate')"
                    :rules="[ val => val && val > 0 || $t('transfers.rate') + ' is required!']"
                    color="primary input-bold"
                    input-class="text-center"
                    type="number"
                    :hint="TransferObj.rate < 0.1 ? Number( 1 / TransferObj.rate).roundToX(transfer_decimals).toString() : ''"
                    @input="calculateOriginOrDestAmount"
                    :readonly="locked_input === 'rate'"
                >
                    <template v-slot:prepend>
                        <q-btn
                            flat
                            round
                            :icon="locked_input === 'rate' ? 'fa fa-lock' : 'fa fa-lock-open'"
                            :color="locked_input === 'origin' ? 'primary' : 'gray'"
                            size="xs"
                            @click="lock('rate')"
                        />
                    </template>

                    <template v-slot:append>
                        <q-btn
                            outline
                            label="Last price"
                            icon="fa fa-refresh"
                            color="positive"
                            size="xs"
                            @click="refreshRates"
                            :disabled="locked_input === 'rate'"
                        />
                    </template>
                </q-input>
            </div> <!-- RATE -->
            <div class="col col-xs-12 col-md-4 col-lg-4">
                <q-input
                    v-model="TransferObj.dest_amount"
                    :label="$t('transfers.dest_amount')"
                    type="number"
                    input-class="text-center input-bold"
                    color="primary"
                    :error="TransferObj.errors.has('dest_amount')"
                    :error-message="TransferObj.errors.get('dest_amount')"
                    :rules="[ val => val && val > 0 || $t('transfers.dest_amount') + ' is required!']"
                    @input="calculateRateOrOriginAmount"
                    :readonly="locked_input === 'dest'"
                >
                    <template v-slot:prepend>
                        <q-btn
                            flat
                            round
                            :icon="locked_input === 'dest' ? 'fa fa-lock' : 'fa fa-lock-open'"
                            :color="locked_input === 'origin' ? 'primary' : 'gray'"
                            size="xs"
                            :disabled="false"
                            @click="lock('dest')"
                        />
                    </template>
                    <template v-slot:append>
                        <span class="text-subtitle1 text-bold text-primary" v-if="editMode">{{ TransferObj.dest_currency }}</span>
                        <q-btn-dropdown
                            v-model="displayDestCurrencies"
                            :label="destCurrency ? destCurrency.symbol : '?'"
                            :icon="destCurrency ? destCurrency.country : ''"
                            flat
                            dense
                            v-else
                        >
                            <q-list>
                                <q-item clickable v-close-popup v-for="to_currency in dest_currencies" :key="to_currency.acronym" @click="setTransferDestCurrency(to_currency)">
                                    <q-item-section avatar>
                                        <q-icon :name="to_currency.country" size="36px" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label>{{to_currency.currency}}</q-item-label>
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </q-btn-dropdown>
                    </template>
                    <template v-slot:hint>
                        <currency-format
                            class="inline-block"
                            :value="TransferObj.dest_amount"
                            :unit="TransferObj.dest_currency"
                            hide-white-spaces
                            hide-zero-decimal
                        />
                    </template>
                </q-input>
            </div> <!-- RECEIVED AMOUNT -->
            <div class="col col-xs-12 col-md-6 col-lg-6">
                <q-input
                    v-model="TransferObj.service_fee"
                    :label="$t('transfers.service_fee')"
                    lazy-rules
                    :error="TransferObj.errors.has('service_fee')"
                    :error-message="TransferObj.errors.get('service_fee')"
                    :rules="[ val => val && val >= 0 || $t('transfers.service_fee')+' is required!']"
                    color="primary"
                    input-class="text-center"
                    type="number"
                >
                    <template v-slot:append>
                        <span class="text-subtitle1 text-bold text-secondary">{{ TransferObj.from_currency }}</span>
                    </template>
                </q-input>
            </div> <!-- SERVICE FEE -->
            <div class="col col-xs-12 col-md-6 col-lg-6">
                <q-input
                    v-model="TransferObj.referral_earnings"
                    :label="$t('transfers.referral_earnings')"
                    lazy-rules
                    :error="TransferObj.errors.has('referral_earnings')"
                    :error-message="TransferObj.errors.get('referral_earnings')"
                    :rules="[ val => val && val >= 0 || $t('transfers.referral_earnings') + ' is required!']"
                    color="primary"
                    input-class="text-center"
                    type="number"
                >
                    <template v-slot:append>
                        <span class="text-subtitle1 text-bold text-secondary">{{ TransferObj.from_currency }}</span>
                    </template>
                </q-input>
            </div> <!-- REFERRAL EARNING -->
            <div class="col col-xs-12 col-md-6 col-lg-6">
                <q-select
                    v-model="recipient"
                    :options="recipients"
                    label="User's recipient"
                    style="width: 100%"
                    color="primary"
                    @input="newRecipient"
                    v-if="editMode"
                >
                    <template v-slot:selected>
                        <q-item v-if="recipient.bank_account_id" class="full-width">
                            <q-item-section>
                                <q-item-label lines="1" class="text-bold text-primary">{{ recipient.name }} ({{ recipient.description }})</q-item-label>
                                <q-item-label caption>{{ recipient.bank }}</q-item-label>
                                <q-item-label class="text-bolder"><b>{{ recipient.label1 }}</b>: {{ recipient.account_number }}</q-item-label>
                                <q-item-label class="text-bolder"><b>{{ recipient.label2 || 'Notes'}}</b>: {{ recipient.notes }}</q-item-label>
                            </q-item-section>
                        </q-item>
                        <q-item v-else-if="TransferObj.recipient.name" class="full-width">
                            <q-item-section>
                                <q-item-label lines="1" class="text-bold text-grey-6">{{ TransferObj.recipient.name }}</q-item-label>
                                <q-item-label caption class="text-grey-6">{{ TransferObj.recipient.bank }}</q-item-label>
                                <q-item-label class="text-bolder text-grey-6"><b>{{ TransferObj.recipient.label1 }}</b>:{{ TransferObj.recipient.account_number }}</q-item-label>
                                <q-item-label class="text-bolder text-grey-6"><b>Notes</b>: {{ TransferObj.recipient.notes }}</q-item-label>
                            </q-item-section>
                            <q-item-section side top>
                                <q-item-label caption>Current</q-item-label>
                            </q-item-section>
                        </q-item>
                        <div v-else></div>
                    </template>
                    <template v-if="recipient.bank_account_id" v-slot:append>
                        <q-icon name="cancel" @click.stop="recipient = resetRecipient" class="cursor-pointer" />
                    </template>
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                            style="border-bottom:1px solid #999"
                        >
                            <q-item-section>
                                <q-item-label lines="1" class="text-bold text-primary">{{ scope.opt.name }} {{scope.opt.description ? '(' + scope.opt.description + ')' : ''}}</q-item-label>
                                <q-item-label caption>{{ scope.opt.bank }}</q-item-label>
                                <q-item-label class="text-bolder"><b>{{ scope.opt.label1 }}</b>: {{ scope.opt.account_number }}</q-item-label>
                                <q-item-label class="text-bolder" v-if="scope.opt.notes"><b> {{ scope.opt.label2 }}</b>: {{ scope.opt.notes }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>

                <q-select
                    v-model="recipient"
                    :options="filtered_recipients"
                    label="User's recipient"
                    style="width: 100%"
                    color="primary"
                    @input="TransferObj.bank_account_id = recipient.bank_account_id"
                    :loading="recipient_loading"
                    v-else
                >
                    <template v-slot:selected>
                        <q-item v-if="TransferObj.bank_account_id" class="full-width">
                            <q-item-section>
                                <q-item-label lines="1" class="text-bold text-primary">{{ recipient.name }} ({{ recipient.description }})</q-item-label>
                                <q-item-label caption>{{ recipient.bank }}</q-item-label>
                                <q-item-label class="text-bolder"><b>{{ recipient.label1 }}</b>: {{ recipient.account_number }}</q-item-label>
                                <q-item-label class="text-bolder"><b>{{ recipient.label2 || 'Notes'}}</b>: {{ recipient.notes }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                            style="border-bottom:1px solid #999"
                        >
                            <q-item-section v-if="! TransferObj.user_id">
                                <q-item-label class="text-grey text-body1">No user selected!</q-item-label>
                            </q-item-section>
                            <q-item-section v-else-if="scope.index + 1 === filtered_recipients.length"
                                            @click="increaseRecipientModalKey">
                                <q-item-label
                                    lines="1"
                                    class="text-bold text-primary"
                                >
                                    <q-icon name="fa fa-plus" color="primary" size="xs"/> {{ scope.opt.name }}
                                </q-item-label>
                            </q-item-section>
                            <q-item-section v-else>
                                <q-item-label lines="1" class="text-bold text-primary">{{ scope.opt.name }} {{scope.opt.description ? '(' + scope.opt.description + ')' : ''}}</q-item-label>
                                <q-item-label caption>{{ scope.opt.bank }}</q-item-label>
                                <q-item-label class="text-bolder"><b>{{ scope.opt.label1 }}</b>: {{ scope.opt.account_number }}</q-item-label>
                                <q-item-label class="text-bolder" v-if="scope.opt.notes"><b> {{ scope.opt.label2 }}</b>: {{ scope.opt.notes }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
            </div> <!-- USER'S RECIPIENT -->
            <div class="col col-xs-12 col-md-6 col-lg-6">
                <q-select
                    use-input
                    use-chips
                    v-model="TransferObj.reason"
                    :options="transfer_reasons"
                    option-value="name"
                    option-label="label"
                    emit-value
                    map-options
                    :label="$t('transfers.table_titles.reason')"
                    :error="TransferObj.errors.has('reason')"
                    :error-message="TransferObj.errors.get('reason')"
                    color="primary"
                    :hint="TransferObj.reason_text"
                    @new-value="newReason"
                    @input="removeReason"
                />
            </div> <!-- REASON -->
            <div class="col col-xs-12 col-sm-6 col-md-6">
                <q-select
                    v-model="TransferObj.status"
                    :options="statuses"
                    emit-value
                    map-options
                    option-value="name"
                    option-label="label"
                    :label="$t('transfers.table_titles.status')"
                    color="primary"
                >
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                        >
                            <q-item-section avatar>
                                <q-icon :name="scope.opt.icon" :color="scope.opt.color"/>
                            </q-item-section>
                            <q-item-section>
                                <q-item-label>{{ scope.opt.label }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
            </div> <!-- STATUS -->
            <div class="col col-xs-12 col-sm-6 col-md-6">
                <q-select
                    v-model="TransferObj.rebit_account_id"
                    :options="rebit_accounts"
                    fill-input
                    option-value="id"
                    option-label="label"
                    clearable
                    emit-value
                    map-options
                    :label="$t('transfers.table_titles.rebit_account')"
                    :error="TransferObj.errors.has('rebit_account_id')"
                    :error-message="TransferObj.errors.get('rebit_account_id')"
                    color="primary"
                    :value="TransferObj.rebit_account_id"
                >
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                        >
                            <q-item-section>
                                <q-item-label>{{scope.opt.label}}</q-item-label>
                                <q-item-label caption>{{ scope.opt.holder }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
            </div> <!-- REBIT ACCOUNT -->
            <div class="col col-xs-12 col-sm-6 col-md-4">
                <q-input
                    v-model="TransferObj.created_at"
                    label="Created At"
                    mask="datetime"
                    :error="TransferObj.errors.has('created_at')"
                    :error-message="TransferObj.errors.get('created_at')"
                    color="primary"
                >
                    <template v-slot:prepend>
                        <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                            <q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
                                <q-date
                                    v-model="TransferObj.created_at"
                                    @input="() => $refs.qDateProxy.hide()"
                                    mask="YYYY-MM-DD HH:mm"
                                    today-btn
                                    emit-immediately
                                />
                            </q-popup-proxy>
                        </q-icon>
                    </template>
                </q-input>
            </div> <!-- CREATED AT -->
            <div class="col col-xs-12 col-sm-6 col-md-4">
                <q-checkbox
                    left-label
                    v-model="TransferObj.notify"
                    label="Notify user by sending email"
                    color="primary"
                />
            </div> <!-- Notification Email -->
            <div class="col col-xs-12 col-sm-6 col-md-4">
                <div class="q-gutter-sm">
                    <q-checkbox
                        left-label
                        v-model="TransferObj.bypass"
                        label="Bypass transfer amount limits"
                        color="primary"
                    />
                </div>
            </div> <!-- BYPASS -->
        </div>
        <div class="row q-col-gutter-md q-mt-md">
            <div class="col-12">
                <span class="text-h6">Total payment: </span> <currency-format
                    class="inline-block text-h6"
                    :value="Number(TransferObj.origin_amount) + Number(TransferObj.service_fee) - Number(TransferObj.referral_earnings)"
                    :unit="TransferObj.origin_currency ? TransferObj.origin_currency : (originCurrency ? originCurrency.symbol : '?')"
                    hide-white-spaces
                    hide-zero-decimal
                />
            </div>
        </div>

        <recipient-modal
            v-model="showRecipientDialog"
            :user="selectedUser"
            :key="recipientModalKey"
            @created="addRecipient"
            :dest-currency="TransferObj.to_currency"
        />
    </form>
</template>
<script>

import { mapActions, mapGetters } from 'vuex'
import User from "Modules/User/resources/models/User"
import Recipient from "Modules/Recipient/resources/models/Recipient"
import RecipientModal from 'Modules/Recipient/resources/components/RecipientModal'

export default {
    name: "AdvancedForm",
    model: {
        prop: 'TransferObj',
        event: 'change'
    },
    props: {
        TransferObj: {
            type: Object,
            required: true
        },
        editMode: {
            type: Boolean,
            default: false
        },
        TransferRecipients: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        ...mapGetters(['exchange_rates', 'crypto_currencies', 'depositableRebitAccountsByCurrency']),
        current_ramp() {
            return this.ramps.find(rate => (rate.from.acronym === this.TransferObj.from_currency) && (rate.to.acronym === this.TransferObj.to_currency))
        },
        transfer_decimals() {
            return this.current_ramp ? this.current_ramp.decimals : 0
        },
        origin_currencies() {
            let currencies = [];
            this.ramps.forEach((item) => {
                if(! currencies.find(value => value.acronym === item.from.acronym)) {
                    currencies.push(item.from)
                }
            })
            return currencies
        },
        dest_currencies() {
            let currencies = [];
            this.ramps.forEach((item) => {
                if((! currencies.find(value => value.acronym === item.to.acronym) && (item.from.acronym === this.TransferObj.from_currency))) {
                    currencies.push(item.to)
                }
            })
            return currencies
        },
        origin_currency_decimals() {
            return this.originCurrency ? this.originCurrency.decimals : 2
        },
        dest_currency_decimals() {
            return this.destCurrency ? this.destCurrency.decimals : 2
        },
        filtered_recipients() {
            let recipients = this.recipients.filter(recipient => recipient.currency === this.TransferObj.to_currency)
            recipients.push({name: 'Add a recipient', bank_account_id: undefined})
            return recipients
        },
        rebit_accounts() {
            let accounts = this.depositableRebitAccountsByCurrency(this.TransferObj.from_currency)
            accounts.push({id: 0, label: 'Let the system choose'});
            return accounts
        },
        from_currency() {
            return this.TransferObj.from_currency
        },
        to_currency() {
            return this.TransferObj.to_currency
        }
    },
    data() {
        return {
            transfer_reasons: [
                {name: 'gift_or_support_family_friends', label: this.$t('transfers.gift_or_support_family_friends')},
                {name: 'travel_holiday_immigration', label: this.$t('transfers.travel_holiday_immigration')},
                {name: 'real_estate_relocation_rent', label: this.$t('transfers.real_estate_relocation_rent')},
                {name: 'university_tuition_fees', label: this.$t('transfers.university_tuition_fees')},
                {name: 'pay_salary', label: this.$t('transfers.pay_salary')},
                {name: 'transfer_business_funds', label: this.$t('transfers.transfer_business_funds')},
                {name: 'charity', label: this.$t('transfers.charity')},
            ],
            statuses: [
                {name:'awaiting', label: 'Awaiting approval', icon: 'fa fa-hourglass-half', color: 'warning'},
                {name:'payment_issue', label: 'Payment issue', icon: 'fa fa-download', color: 'pink'},
                {name:'paid', label: 'Paid', icon: 'fa fa-file-invoice-dollar', color: 'blue'},
                {name:'transfer_issue', label: 'Transfer issue', icon: 'fa fa-upload', color: 'pink'},
                {name:'transferred', label: 'Done', icon: 'fa fa-check-circle', color: 'positive'},
                {name:'canceled', label: 'Cancelled', icon: 'fa fa-window-close', color: 'negative'},
            ],
            originCurrency: null,
            destCurrency: null,
            stringOptions: [],
            ramps: [],
            recipients: [],
            users: [],
            recipient: this.resetRecipient(),
            displayOrgCurrencies: false,
            rewards_loading: true,
            displayDestCurrencies: false,
            recipient_loading: false,
            recipientModalKey: 0,
            showRecipientDialog: false,
            selectedUser: undefined,
            locked_input: 'rate'
        }
    },
    methods: {
        ...mapActions(['set_currencies']),
        lock(value) {
            this.locked_input = value
        },
        grabUser(user) {
            this.selectedUser = user
            this.TransferObj.user_id = user.id
            this.fetchRecipients()
        },
        filterUsers(val, update, abort) {
            if (val.length < 3) {
                abort()
                return
            }
            User.list(`q=${val}`).then(response => {
                this.stringOptions = response
                update(() => {
                    this.users = this.stringOptions
                })
            }).catch(data => {
                abort()
            })
        },
        calculateRateOrDestAmount() {
            if (this.locked_input === 'rate') {
                this.TransferObj.dest_amount = this.current_ramp ? Number(this.TransferObj.origin_amount * this.TransferObj.rate).roundToX(this.dest_currency_decimals) : this.TransferObj.dest_amount
            } else {
                this.TransferObj.rate = this.current_ramp ? Number(this.TransferObj.dest_amount / this.TransferObj.origin_amount) : 0
            }
        },
        setTransferOriginCurrency(currency) {
            this.originCurrency = currency
            this.TransferObj.from_currency = currency.acronym
            this.TransferObj.origin_currency = currency.symbol
            this.setTransferDestCurrency(this.dest_currencies[0])
        },
        setTransferDestCurrency(currency) {
            this.destCurrency = currency
            this.TransferObj.to_currency = currency.acronym
            this.TransferObj.dest_currency = currency.symbol
        },
        calculateRateOrOriginAmount() {
            if (this.locked_input === 'rate') {
                this.TransferObj.origin_amount = this.current_ramp ? Number(this.TransferObj.dest_amount / this.TransferObj.rate).roundToX(this.origin_currency_decimals) : this.TransferObj.origin_amount
            } else {
                this.TransferObj.rate = this.current_ramp ? Number(this.TransferObj.dest_amount / this.TransferObj.origin_amount) : 0
            }
        },
        calculateOriginOrDestAmount() {
            if (this.locked_input === 'origin') {
               this.TransferObj.dest_amount = Number(this.TransferObj.origin_amount * this.TransferObj.rate).roundToX(this.dest_currency_decimals)
            } else {
                this.TransferObj.origin_amount = Number(this.TransferObj.dest_amount / this.TransferObj.rate).roundToX(this.origin_currency_decimals)
            }
        },
        setRate() {
            this.TransferObj.rate = this.current_ramp.rate < 0.1 ? this.current_ramp.rate : this.current_ramp.rate.roundToX(this.transfer_decimals).toString()
        },
        refreshRates() {
            this.ramps = this.exchange_rates
            this.setRate()
        },
        changeRamp() {
            this.TransferObj.service_fee = this.current_ramp.fees
            this.locked_input = 'rate'
            this.setRate()
            this.calculateRateOrDestAmount()
        },
        newReason (val, done) {
            this.TransferObj.reason_text = val
            done('other', 'add-unique')
        },
        removeReason(details) {
            if (!details) {
                this.TransferObj.reason_text = ''
            }
        },
        resetRecipient() {
            return {
                name: undefined,
                description: undefined,
                bank: undefined,
                label1: undefined,
                account_number: undefined,
                label2: undefined
            }
        },
        fetchRecipients() {
            if (!this.TransferObj.user_id) {
                return
            }

            this.recipient_loading = true
            Recipient.all(`${this.TransferObj.user_id ? `user_id=${this.TransferObj.user_id}&` : ''}sort=id%7Casc&page=1&per_page=16&filter=`, ({data}) => {
                this.recipient_loading = false

                this.recipients = data.flatMap((recipient) => {
                    return recipient.bank_accounts.flatMap((bank_account) => {
                        return {
                            bank_account_id: bank_account.id,
                            name: recipient.name,
                            bank: bank_account.bank,
                            account_number: bank_account.number,
                            currency: recipient.currency_acronym,
                            label1: bank_account.info_label1,
                            label2: bank_account.info_label2,
                            notes: bank_account.notes,
                            type: bank_account.type,
                            bank_required: bank_account.bank_required
                        }
                    })
                })
            })
        },
        increaseRecipientModalKey() {
            this.recipientModalKey += 1
            this.showRecipientDialog = true
        },
        addRecipient(data) {
            this.recipient = {
                bank_account_id: data.bank_accounts[0].id,
                name: data.name,
                bank: data.bank_accounts[0].bank,
                account_number: data.bank_accounts[0].number,
                currency: data.currency_acronym,
                label1: data.bank_accounts[0].info_label1,
                label2: data.bank_accounts[0].info_label2,
                notes: data.bank_accounts[0].notes,
                type: data.bank_accounts[0].type,
                bank_required: data.bank_accounts[0].bank_required
            }
            this.recipients.push(this.recipient)
            this.TransferObj.bank_account_id = this.recipient.bank_account_id
            this.recipientModalKey += 1
        },
        newRecipient(value) {
            this.TransferObj.bank_account_id = value.bank_account_id
            this.$emit('change', this.TransferObj)
        }
    },
    created() {
        if (! this.crypto_currencies.length) {
            this.loading = true
            this.set_currencies().then(() => {this.loading = false}).catch(() => {this.loading = false})
        }
    },
    mounted() {
        this.ramps = this.exchange_rates
    },
    watch: {
        TransferRecipients(val) {
            this.recipients = val
        },
        current_ramp(val) {
            if (val && !this.editMode) {
                this.changeRamp()
            }
        },
        from_currency(val) {
            this.originCurrency = this.origin_currencies.find(currency => currency.acronym === val)
        },
        to_currency(val) {
            this.destCurrency = this.dest_currencies.find(currency => currency.acronym === val)
        }
    },
    components: {RecipientModal},
}
</script>
<style>

</style>
